import { Button } from "@/components/ui/button";
import { ArrowRight, Home, Lightbulb, TreePine } from "lucide-react";
import { Link } from "react-router-dom";
import CanonicalTag from "@/components/CanonicalTag";

const ContemporaryADUDesignRedwoodCity = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <CanonicalTag url="https://udaadu.com/contemporary-adu-design-redwood-city" />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-6">
            Contemporary ADU Design in Redwood City
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Create sophisticated living spaces in Redwood City with contemporary ADU designs that blend current trends with timeless functionality and comfort.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Your Contemporary ADU Quote
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg">
              View Contemporary Portfolio
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Contemporary ADU Design in Redwood City?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-lg bg-card border">
              <Home className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Current Trends</h3>
              <p className="text-muted-foreground">
                Incorporating the latest design trends while ensuring longevity and appeal in Redwood City's evolving real estate market.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Lightbulb className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Smart Design</h3>
              <p className="text-muted-foreground">
                Intelligent layouts that adapt to modern lifestyles while maximizing comfort and functionality in every square foot.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <TreePine className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Natural Integration</h3>
              <p className="text-muted-foreground">
                Seamless connection between indoor and outdoor spaces, perfect for Redwood City's beautiful climate and surroundings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-3xl font-bold mb-6">Contemporary ADU Design Excellence in Redwood City</h2>
          
          <p className="text-lg text-muted-foreground mb-6">
            Redwood City's dynamic growth and proximity to Silicon Valley make it an ideal location for contemporary ADU designs that reflect current lifestyle needs. Our contemporary approach balances modern convenience with comfortable living, creating spaces that feel both cutting-edge and welcoming.
          </p>

          <h3 className="text-2xl font-semibold mb-4">Contemporary Design Features</h3>
          <ul className="space-y-2 text-muted-foreground mb-6">
            <li>• Mixed textures and premium materials</li>
            <li>• Flexible multi-purpose spaces</li>
            <li>• Energy-efficient systems and appliances</li>
            <li>• Indoor-outdoor living integration</li>
            <li>• Statement lighting and fixtures</li>
            <li>• Smart home technology integration</li>
          </ul>

          <h3 className="text-2xl font-semibold mb-4">Redwood City Contemporary ADU Advantages</h3>
          <p className="text-muted-foreground mb-6">
            Contemporary ADUs in Redwood City offer excellent rental potential due to the city's growing tech workforce and families seeking modern amenities. These designs appeal to professionals who appreciate current trends while providing the functionality needed for comfortable Peninsula living.
          </p>

          <div className="bg-muted/20 p-6 rounded-lg mb-8">
            <h4 className="text-xl font-semibold mb-3">Ready to Build Your Contemporary ADU in Redwood City?</h4>
            <p className="text-muted-foreground mb-4">
              Our design team stays current with the latest trends while ensuring your ADU meets all Redwood City requirements and exceeds expectations.
            </p>
            <Button className="group">
              Start Your Contemporary ADU Project
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Enhance Your Redwood City Property Today</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Discover how contemporary ADU design can transform your property and lifestyle in Redwood City.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Free Contemporary ADU Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">Contact Our Design Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContemporaryADUDesignRedwoodCity;